// Copyright 2025, University of Maryland, All Rights Reserved
import { Button } from '@/components/ui'
import { Loader2, RefreshCw } from 'lucide-react'

import type { TimestampInfo } from '../types'
import { formatTimestamp } from '../utils/formatters'

interface StatsHeaderProps {
  localCount: number
  sharedCount: number
  onRefresh: () => void
  disabled: boolean
  isRefreshing: boolean
  timestamp: TimestampInfo
}

export function StatsHeader({
  localCount,
  sharedCount,
  onRefresh,
  disabled,
  isRefreshing,
  timestamp,
}: StatsHeaderProps): JSX.Element {
  const formatted = formatTimestamp(timestamp.lastUpdated)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-col gap-1">
        <p data-testid="kb-local-count" className="text-[13px] text-slate-500">
          {localCount} {localCount === 1 ? 'item' : 'items'} · local
          {sharedCount > 0 && ` · ${sharedCount} shared`}
        </p>
        {(formatted || timestamp.updatedBy) && (
          <p className="text-xs text-slate-400">
            {formatted && `Updated ${formatted}`}
            {formatted && timestamp.updatedBy && ' · '}
            {timestamp.updatedBy && `by ${timestamp.updatedBy}`}
          </p>
        )}
      </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={disabled}
          className="h-[34px] shrink-0 rounded-lg border-slate-200 px-3.5 text-[13px] font-medium shadow-[0_1px_2px_rgba(0,0,0,.04)] transition-[background,border-color,transform] duration-150 hover:border-slate-300 hover:bg-slate-50 active:scale-[.97]"
          data-testid="kb-refresh"
        >
          {isRefreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
    </div>
  )
}
